import { DEFAULT_LANDING_ROUTE } from "../constants/appRoutes";
import { isSponsorCroUserRecord } from "../constants/userTypeLabels";
import { SIDEBAR_ORG_MODULES } from "./sidebarOrgModuleFilter";
import api from "../services/apiClient";
import API_ENDPOINTS from "../config/apiEndpoints";
import { storeUserData } from "../services/apiService";

export const SPONSOR_CRO_EMPTY_HOME_ROUTE = "/shared-home";

/** Shared chrome every Sponsor/CRO user can open — same as internal staff. */
const SHARED_STAFF_PATHS = [
  "/accept-terms",
  "/change-credentials-required",
  "/organizations",
  SPONSOR_CRO_EMPTY_HOME_ROUTE,
  DEFAULT_LANDING_ROUTE,
  "/faq",
  "/my-tickets",
];

const MODULE_PATH_PREFIXES = {
  [SIDEBAR_ORG_MODULES.ISF]: ["/isf"],
  [SIDEBAR_ORG_MODULES.EPSF]: ["/epsf"],
  [SIDEBAR_ORG_MODULES.STUDY_USER]: ["/studymanagement", "/studyprofile"],
  [SIDEBAR_ORG_MODULES.EPLANNER]: ["/eplanner"],
  [SIDEBAR_ORG_MODULES.CAPA]: ["/capa"],
  [SIDEBAR_ORG_MODULES.QMS]: ["/qms"],
  [SIDEBAR_ORG_MODULES.PARTICIPANT_MANAGER]: ["/participant"],
  [SIDEBAR_ORG_MODULES.ANALYTICS]: ["/analytics"],
};

export function isSponsorCroSession(userData) {
  if (!userData) return false;
  return isSponsorCroUserRecord(
    userData.IsCRO ?? userData.IsCro ?? userData.isCRO,
  );
}

/** OtherOrgLink OrgType only. Role "CRO" is not the same as legacy tblCRO OrgType. */
export function resolveOtherOrgLinkType(item) {
  const raw = String(
    item?.OrgType ?? item?.orgType ?? item?.Orgtype ?? item?.type ?? "",
  ).trim();
  if (isSponsorCroUserRecord(raw) || raw.toUpperCase() === "CRO") {
    return "CRO";
  }
  return raw || "Site";
}

export function normalizeGrantedModules(raw) {
  const list = Array.isArray(raw)
    ? raw
    : raw == null || raw === ""
      ? []
      : String(raw)
          .split(",")
          .map((t) => t.trim());
  return [
    ...new Set(
      list
        .map((m) => String(m ?? "").trim())
        .filter(Boolean)
        .map((m) => m.toLowerCase()),
    ),
  ];
}

export function getGrantedModules(userData) {
  if (!isSponsorCroSession(userData)) return [];
  return normalizeGrantedModules(
    userData.GrantedModules ?? userData.grantedModules,
  );
}

export function hasGrantedModule(userData, moduleKey) {
  const key = String(moduleKey || "")
    .trim()
    .toLowerCase();
  if (!key) return false;
  return getGrantedModules(userData).includes(key);
}

export function resolveSponsorCroLandingRoute(_userData) {
  return SPONSOR_CRO_EMPTY_HOME_ROUTE;
}

export function isSponsorCroAuthPath(pathname) {
  const path = String(pathname || "").toLowerCase();
  return SHARED_STAFF_PATHS.some(
    (allowed) => path === allowed || path.startsWith(`${allowed}/`),
  );
}

function isOwnUserProfilePath(pathname, userData) {
  const staffId = String(userData?.StaffId || "")
    .trim()
    .toLowerCase();
  if (!staffId) return false;
  const path = String(pathname || "").toLowerCase();
  const own = `/userprofile/${staffId}`;
  return path === own || path.startsWith(`${own}/`);
}

export function isSponsorCroPathAllowed(pathname, userData) {
  if (!isSponsorCroSession(userData)) return true;
  if (isSponsorCroAuthPath(pathname)) return true;
  if (isOwnUserProfilePath(pathname, userData)) return true;
  const path = String(pathname || "").toLowerCase();
  const modules = getGrantedModules(userData);
  return modules.some((key) => {
    const prefixes = MODULE_PATH_PREFIXES[key] || [];
    return prefixes.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`),
    );
  });
}

function rowsFromUnknown(raw) {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.Table)) return raw.Table;
  if (Array.isArray(raw?.Rows)) {
    const cols = (raw.Columns || raw.columns || []).map(
      (c) => c?.ColumnName || c?.columnName || c?.name || "",
    );
    if (cols.length && Array.isArray(raw.Rows[0])) {
      return raw.Rows.map((values) => {
        const row = {};
        cols.forEach((name, i) => {
          if (name) row[name] = values?.[i];
        });
        return row;
      });
    }
    return raw.Rows;
  }
  return [];
}

export function getExternalEntityAccessRows(userData) {
  const raw =
    userData?.ExternalEntityAccess ??
    userData?.externalEntityAccess ??
    userData?.ExternalStaffISFDocuments ??
    [];
  return rowsFromUnknown(raw);
}

function grantModule(row) {
  return String(row?.Module ?? row?.module ?? "isf")
    .trim()
    .toLowerCase() || "isf";
}

function grantStudyId(row) {
  return String(row?.StudyId ?? row?.studyId ?? "")
    .trim()
    .toLowerCase();
}

function grantSiteId(row) {
  return String(row?.SiteId ?? row?.siteId ?? "")
    .trim()
    .toLowerCase();
}

function grantScope(row) {
  return String(row?.ScopeType ?? row?.scopeType ?? "")
    .trim()
    .toUpperCase();
}

function grantIsValid(row) {
  if (row == null) return false;
  const active = row.IsActive ?? row.isActive;
  if (active === false || active === 0 || active === "0") return false;
  const until = row.AccessUntil ?? row.accessUntil;
  if (until == null || String(until).trim() === "") return true;
  const end = new Date(until);
  if (Number.isNaN(end.getTime())) return true;
  return end.getTime() >= Date.now();
}

export function getValidIsfGrantsForStudy(userData, studyId) {
  const sid = String(studyId || "")
    .trim()
    .toLowerCase();
  if (!sid || !isSponsorCroSession(userData)) return [];
  return getExternalEntityAccessRows(userData).filter(
    (row) =>
      grantIsValid(row) &&
      grantModule(row) === SIDEBAR_ORG_MODULES.ISF &&
      grantStudyId(row) === sid,
  );
}

export function getCroGrantedCategoriesForStudy(userData, studyId) {
  const grants = getValidIsfGrantsForStudy(userData, studyId);
  const cats = new Set();
  grants.forEach((row) => {
    const raw = String(row?.Category ?? row?.category ?? "")
      .trim()
      .toLowerCase();
    if (raw === "epsf") cats.add("ePSF");
    else if (raw === "eisf") cats.add("eISF");
    else if (!raw) {
      // Legacy rows without category — treat as eISF.
      cats.add("eISF");
    }
  });
  return ["eISF", "ePSF"].filter((cat) => cats.has(cat));
}

/**
 * Binder types the current user may open for a study.
 * Sponsor/CRO: only shared grant categories. Internal: study template flags.
 */
export function resolveIsfOpenBinderCategories(userData, study) {
  const studyId = study?.StudyId ?? study?.value ?? study?.studyId;
  let hasEisf = false;
  let hasEpsf = false;

  if (isSponsorCroSession(userData)) {
    const grantedCats = getCroGrantedCategoriesForStudy(userData, studyId);
    hasEisf = grantedCats.includes("eISF");
    hasEpsf = grantedCats.includes("ePSF");
  } else {
    hasEisf = Boolean(study?.HaseISFTemplate ?? study?.haseISF);
    hasEpsf = Boolean(study?.HasePSFTemplate ?? study?.hasePSF);
  }

  const categoryOptions = [];
  if (hasEisf) categoryOptions.push({ label: "eISF", value: "eISF" });
  if (hasEpsf) categoryOptions.push({ label: "ePSF", value: "ePSF" });

  return {
    hasEisf,
    hasEpsf,
    categoryOptions,
    defaultCategory: categoryOptions[0] || null,
  };
}

export function croHasWholeStudyIsfGrant(userData, studyId) {
  return getValidIsfGrantsForStudy(userData, studyId).some(
    (row) => grantScope(row) === "WHOLE_STUDY" && !grantSiteId(row),
  );
}

function isFolderScopeType(scope) {
  const s = String(scope || "")
    .trim()
    .toUpperCase();
  return s === "FOLDER" || s === "SHARE_FOLDER" || s === "SHARE_FILES";
}

function grantFolderId(row) {
  return String(row?.FolderId ?? row?.folderId ?? "")
    .trim()
    .toLowerCase();
}

function grantCategory(row) {
  return String(row?.Category ?? row?.category ?? "")
    .trim()
    .toLowerCase();
}

/**
 * Folder IDs granted for folder-scoped CRO access.
 * Returns null when CRO has whole-study or site-level access (no folder filter).
 * Returns empty Set when folder-scoped but no FolderIds (deny all placeholders).
 */
export function getCroGrantedFolderIdsForStudy(
  userData,
  studyId,
  { siteId = null, category = null } = {},
) {
  if (!isSponsorCroSession(userData)) return null;
  if (croHasWholeStudyIsfGrant(userData, studyId)) return null;

  const grants = getValidIsfGrantsForStudy(userData, studyId);
  if (!grants.length) return new Set();

  const siteKey = String(siteId || "")
    .trim()
    .toLowerCase();
  const catKey = String(category || "")
    .trim()
    .toLowerCase();

  const siteLevel = grants.some((row) => {
    if (grantScope(row) !== "SITE") return false;
    const rowSite = grantSiteId(row);
    if (!siteKey) return true;
    return !rowSite || rowSite === siteKey;
  });
  if (siteLevel) return null;

  const folderIds = new Set();
  let sawFolderScope = false;
  grants.forEach((row) => {
    if (!isFolderScopeType(grantScope(row))) return;
    if (catKey) {
      const rowCat = grantCategory(row);
      if (rowCat && rowCat !== catKey && !(catKey === "eisf" && !rowCat)) {
        return;
      }
    }
    const rowSite = grantSiteId(row);
    if (siteKey && rowSite && rowSite !== siteKey) return;
    sawFolderScope = true;
    const folderId = grantFolderId(row);
    if (folderId) folderIds.add(folderId);
  });

  if (!sawFolderScope) return null;
  return folderIds;
}

/** Keep placeholders whose FolderIdRef is in the CRO folder grant set. */
export function filterPlaceholdersForCroFolderScope(
  placeholders,
  userData,
  studyId,
  { siteId = null, category = null } = {},
) {
  const list = Array.isArray(placeholders) ? placeholders : [];
  const allowed = getCroGrantedFolderIdsForStudy(userData, studyId, {
    siteId,
    category,
  });
  if (allowed == null) return list;
  if (allowed.size === 0) return [];
  return list.filter((row) => {
    const folderId = String(
      row?.FolderIdRef ??
        row?.folderIdRef ??
        row?.TemplateFolderId ??
        row?.FolderId ??
        "",
    )
      .trim()
      .toLowerCase();
    return folderId && allowed.has(folderId);
  });
}

export function getCroGrantedSiteIdsForStudy(userData, studyId) {
  if (croHasWholeStudyIsfGrant(userData, studyId)) return null;
  const grants = getValidIsfGrantsForStudy(userData, studyId);
  if (grants.some((row) => !grantSiteId(row))) return null;
  const ids = new Set();
  grants.forEach((row) => {
    const siteId = grantSiteId(row);
    if (siteId) ids.add(siteId);
  });
  return ids;
}

export function getCroGrantedStudyIds(userData) {
  if (!isSponsorCroSession(userData)) return null;
  const ids = new Set();
  getExternalEntityAccessRows(userData).forEach((row) => {
    if (!grantIsValid(row) || grantModule(row) !== SIDEBAR_ORG_MODULES.ISF) {
      return;
    }
    const studyId = grantStudyId(row);
    if (studyId) ids.add(studyId);
  });
  return ids;
}

export function filterStudiesForSponsorCro(studies, userData) {
  if (!isSponsorCroSession(userData)) return studies || [];
  // Binder/study APIs already return only granted studies for Sponsor/CRO.
  // Do not re-filter against the login snapshot — new assignments would disappear
  // until the external user signed in again.
  return studies || [];
}

/**
 * Live GetValid grants for the signed-in Sponsor/CRO user.
 * Updates session so site filters match the database after admin changes.
 */
export async function refreshSponsorCroLiveGrants(userData) {
  if (!isSponsorCroSession(userData)) return userData;
  const organizationId = userData?.OrganizationId;
  const staffId = userData?.StaffId;
  if (!organizationId || !staffId) return userData;

  try {
    const response = await api.post(API_ENDPOINTS.USER.GET_EXTERNAL_ENTITY_ACCESS, {
      OrganizationId: organizationId,
      StaffId: staffId,
      Action: "GetValid",
    });
    if (Number(response?.success) !== 1) return userData;

    const accessRows = rowsFromUnknown(response?.data ?? []);
    const fromRows = [
      ...new Set(
        accessRows
          .filter(grantIsValid)
          .map((row) => grantModule(row))
          .filter(Boolean),
      ),
    ];
    const prior = normalizeGrantedModules(
      userData.GrantedModules ?? userData.grantedModules,
    );
    const effectiveGranted = [
      ...new Set([...(fromRows.length ? fromRows : prior)]),
    ];
    const next = {
      ...userData,
      ExternalEntityAccess: accessRows,
      Modules: [],
      Permission: aggregateEpsfPermissionsFromGrants(accessRows),
      GrantedModules: effectiveGranted,
    };
    storeUserData(next);
    return next;
  } catch (err) {
    console.error("refreshSponsorCroLiveGrants", err);
    return userData;
  }
}

/**
 * Whether live session grants still allow this study (+ optional category/site).
 */
export function croHasLiveIsfAccessToStudy(
  userData,
  studyId,
  { category = "", siteId = "" } = {},
) {
  if (!isSponsorCroSession(userData)) return true;
  const sid = String(studyId || "")
    .trim()
    .toLowerCase();
  if (!sid) return false;

  const grants = getValidIsfGrantsForStudy(userData, studyId);
  if (!grants.length) return false;

  const cat = String(category || "")
    .trim()
    .toLowerCase();
  if (cat) {
    const catOk = grants.some((row) => {
      const rowCat = String(row?.Category ?? row?.category ?? "")
        .trim()
        .toLowerCase();
      return !rowCat || rowCat === cat;
    });
    if (!catOk) return false;
  }

  if (croHasWholeStudyIsfGrant(userData, studyId)) return true;

  const site = String(siteId || "")
    .trim()
    .toLowerCase();
  if (!site) {
    // Deep-link without site: any remaining site/folder grant for the study is enough.
    return true;
  }

  const grantedSites = getCroGrantedSiteIdsForStudy(userData, studyId);
  if (grantedSites == null) return true;
  if (grantedSites.size === 0) return false;
  return grantedSites.has(site);
}

export function filterSidebarMenuByGrantedModules(menuItems, userData) {
  const granted = new Set(getGrantedModules(userData));
  return (menuItems || [])
    .filter((menu) => menu?.orgModule && granted.has(String(menu.orgModule)))
    .map((menu) => {
      let subItems = Array.isArray(menu.subItems) ? menu.subItems : [];
      if (menu.orgModule === SIDEBAR_ORG_MODULES.ISF) {
        subItems = subItems.filter((sub) => sub?.path === "/isf");
      }
      if (menu.orgModule === SIDEBAR_ORG_MODULES.EPSF) {
        /* Permission-driven children — Sidebar already filters by Permission keys. */
        subItems = subItems.filter((sub) => {
          const path = String(sub?.path || "");
          return path === "/epsf" || path.startsWith("/epsf/");
        });
      }
      return { ...menu, subItems };
    })
    .filter((menu) => (menu.subItems || []).length > 0);
}

function aggregateEpsfPermissionsFromGrants(accessRows) {
  const keys = new Set();
  (accessRows || []).forEach((row) => {
    if (!grantIsValid(row)) return;
    if (grantModule(row) !== SIDEBAR_ORG_MODULES.EPSF) return;
    String(row?.Permissions ?? row?.permissions ?? "")
      .split(/[,;]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((k) => keys.add(k));
  });
  return [...keys].join(",");
}

export function applySponsorCroLoginSession(userData) {
  if (!isSponsorCroSession(userData)) return userData;
  const accessRows = getExternalEntityAccessRows(userData);
  const granted = normalizeGrantedModules(
    userData.GrantedModules ?? userData.grantedModules,
  );
  const fromRows = [
    ...new Set(
      accessRows
        .filter(grantIsValid)
        .map((row) => grantModule(row))
        .filter(Boolean),
    ),
  ];
  /* Prefer live grant modules; union with login GrantedModules so binder+epsf coexist. */
  const effectiveGranted = [...new Set([...granted, ...fromRows])];
  const epsfPermission = aggregateEpsfPermissionsFromGrants(accessRows);
  return {
    ...userData,
    Modules: [],
    /* External users: Permission comes only from Module=epsf grant Permissions CSV. */
    Permission: epsfPermission,
    ExternalEntityAccess: accessRows,
    GrantedModules: effectiveGranted,
  };
}

/** Study IDs from active Module=epsf grants (may be StudyIdRef or StudyScheduleId). */
export function getEpsfGrantedStudyIds(userData) {
  const ids = new Set();
  getExternalEntityAccessRows(userData).forEach((row) => {
    if (!grantIsValid(row)) return;
    if (grantModule(row) !== SIDEBAR_ORG_MODULES.EPSF) return;
    const sid = grantStudyId(row);
    if (sid) ids.add(sid);
  });
  return ids;
}

/** Site IDs granted for a study under Module=epsf. Empty set + wholeStudy=true → all sites. */
export function getEpsfGrantedSiteScope(userData, studyId) {
  const studyKey = String(studyId || "")
    .trim()
    .toLowerCase();
  const siteIds = new Set();
  let wholeStudy = false;
  if (!studyKey) return { siteIds, wholeStudy };
  getExternalEntityAccessRows(userData).forEach((row) => {
    if (!grantIsValid(row)) return;
    if (grantModule(row) !== SIDEBAR_ORG_MODULES.EPSF) return;
    if (grantStudyId(row) !== studyKey) return;
    const scope = grantScope(row);
    const site = grantSiteId(row);
    if (!site || scope === "WHOLE_STUDY") {
      wholeStudy = true;
      return;
    }
    siteIds.add(site);
  });
  return { siteIds, wholeStudy };
}

/** Study/Site-scoped ePSF permission check for Sponsor/CRO sessions. */
export function sponsorCroHasEpsfPermission(
  userData,
  permissionKey,
  { studyId = null, siteId = null } = {},
) {
  if (!isSponsorCroSession(userData) || !permissionKey) return false;
  const needle = String(permissionKey).trim().toUpperCase();
  const studyKey = String(studyId || "")
    .trim()
    .toLowerCase();
  const siteKey = String(siteId || "")
    .trim()
    .toLowerCase();

  return getExternalEntityAccessRows(userData).some((row) => {
    if (!grantIsValid(row)) return false;
    if (grantModule(row) !== SIDEBAR_ORG_MODULES.EPSF) return false;
    if (studyKey && grantStudyId(row) !== studyKey) return false;
    if (siteKey) {
      const rowSite = grantSiteId(row);
      const scope = grantScope(row);
      if (rowSite && rowSite !== siteKey && scope !== "WHOLE_STUDY") return false;
    }
    return String(row?.Permissions ?? row?.permissions ?? "")
      .split(/[,;]+/)
      .map((s) => s.trim().toUpperCase())
      .includes(needle);
  });
}
